import { Show } from "solid-js";
import { LogIn } from "lucide-solid";
import { t } from "@/shared/i18n";
import { login } from "@/auth/msal";
import { sessionExpired } from "@/auth/session";

/**
 * Shown when the sign-in has lapsed: a message and the one action that resolves it.
 *
 * <p>Deliberately not a toast. A toast dismisses itself after four seconds and offers nothing to press,
 * so a lapsed session read as "the order failed, try again" — a retry that fails identically every time
 * because nothing about the request changes. Deliberately not an automatic redirect either: being thrown
 * out to a login screen mid-task, with no statement of what happened, is its own kind of confusing.</p>
 *
 * <p>Sits outside the router's Suspense boundary alongside the consent banner, because the session can
 * lapse on any route and the answer is the same everywhere.</p>
 */
export function SessionExpiredNotice() {
  return (
    <Show when={sessionExpired()}>
      <div
        class="fixed inset-x-0 bottom-0 z-50 border-t border-amber-500/40 bg-zinc-900/95 px-4 py-4 backdrop-blur"
        role="alert"
        aria-live="assertive"
      >
        <div class="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="font-semibold text-amber-300">{t("session.expiredTitle")}</p>
            <p class="text-sm text-zinc-300">{t("session.expiredBody")}</p>
          </div>
          <button
            type="button"
            onClick={() => void login()}
            class="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2 font-semibold text-zinc-950 transition hover:bg-amber-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          >
            <LogIn size={18} aria-hidden="true" />
            {t("session.signInAgain")}
          </button>
        </div>
      </div>
    </Show>
  );
}
